import styled from 'styled-components';

import { SupplierSubscriptionPlan } from '../../../types/SupplierGroupSubscriptionMessage';
import { Text } from '../../cdl/Text/Text';
import { translate, TranslationKeyEn } from '../../common/helpers/translate.helper';
import { IconCheck } from '../../common/icons/cdl/Check';
import { PartlyBoldTranslation } from '../../common/PartlyBoldTranslation';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';

const FeatureList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: ${theme.space[4]};
    margin: 0;
    padding: 0;
    list-style: none;

    li {
        display: flex;
        gap: ${theme.space[8]};
        font-size: ${theme.fontSizes[14]};
        line-height: ${theme.lineHeights[26]};
        color: ${theme.colors.foreground.default};
    }

    li svg {
        flex-shrink: 0;
        margin-top: ${theme.space[4]};
    }
`;

const SUPPLIER_SUBSCRIPTION_PLAN_FEATURES: Record<
    SupplierSubscriptionPlan,
    { title: TranslationKeyEn; features: TranslationKeyEn[] }
> = {
    [SupplierSubscriptionPlan.BASIC]: {
        title: 'supplierSubscriptionPlans.includes',
        features: [
            'supplierSubscriptionPlans.unlimitedUsers',
            'supplierSubscriptionPlans.enquiriesAndOrders',
            'supplierSubscriptionPlans.oneOfferPerEnquiry',
            'supplierSubscriptionPlans.standardSupport',
            'supplierSubscriptionPlans.existingSetup',
        ],
    },
    [SupplierSubscriptionPlan.PROFESSIONAL]: {
        title: 'supplierSubscriptionPlans.professionalIncludes',
        features: [
            'supplierSubscriptionPlans.multipleOffers',
            'supplierSubscriptionPlans.scheduleInsights',
            'supplierSubscriptionPlans.alternativePorts',
            'supplierSubscriptionPlans.prioritySupport',
            'supplierSubscriptionPlans.threeCompanies',
            'supplierSubscriptionPlans.singleSignOnAddOn',
        ],
    },
    [SupplierSubscriptionPlan.ENTERPRISE]: {
        title: 'supplierSubscriptionPlans.enterpriseIncludes',
        features: [
            'supplierSubscriptionPlans.unlimitedCompanies',
            'supplierSubscriptionPlans.singleSignOnIncluded',
            'supplierSubscriptionPlans.standardApiAccess',
            'supplierSubscriptionPlans.customIntegrationOnRequest',
            'supplierSubscriptionPlans.serviceCommitments',
        ],
    },
};

interface SupplierSubscriptionPlanFeaturesProps {
    plan: SupplierSubscriptionPlan;
}

export const SupplierSubscriptionPlanFeatures = ({ plan }: SupplierSubscriptionPlanFeaturesProps) => (
    <Box
        display="flex"
        flexDirection="column"
        gap={12}
        paddingTop={16}
        borderTop="1px solid"
        borderColor="border.muted"
    >
        <Text variant="fieldLabel">{translate(SUPPLIER_SUBSCRIPTION_PLAN_FEATURES[plan].title)}</Text>
        <FeatureList>
            {SUPPLIER_SUBSCRIPTION_PLAN_FEATURES[plan].features.map((key) => (
                <li key={key}>
                    <IconCheck width={16} height={16} color={theme.colors.accent.foreground} />
                    <Text>
                        <PartlyBoldTranslation translation={translate(key)} fontWeight={600} />
                    </Text>
                </li>
            ))}
        </FeatureList>
    </Box>
);
